import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { useTranslation } from '@root/shared/localization'

import type { TransferFlowReatom } from '../../../atoms'
import type { ClassNameProps } from '@root/shared/react'

type EstimatedChangesProps = {
  model: TransferFlowReatom
} & ClassNameProps

export const EstimatedChanges: React.FC<EstimatedChangesProps> = ({
  className,
  model,
}) => {
  const { t } = useTranslation()

  const [currency] = useAtom(model.currency.currencyAtom)
  const [amount] = useAtom(model.amount.amountAtom)
  const wallet = model.wallet
  const [amountUsd] = useAtom(model.amount.amountUsdStateAtom)
  const [estimatedBalance] = useAtom(model.estimatedBalanceAfterStateAtom)
  const [estimatedBalanceUsd] = useAtom(model.estimatedBalanceAfterUsdAtom)

  return (
    <Root className={className}>
      <EstimatedLabel>{t('transfer.estimated-changes')}:</EstimatedLabel>
      <Block>
        <SendContainer>
          <Label>{t('transfer.you-send')}</Label>
          <Amount>
            <span>- </span>
            <Numeric
              value={amount}
              preset="currency-amount-short"
              currency={currency.ticker}
            />
          </Amount>
          <AmountUsd>
            {amountUsd.value ? (
              <Numeric
                value={amountUsd.value}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              <Skeleton height={12} width={50} paddingBlock={1} />
            )}
          </AmountUsd>
        </SendContainer>
        <BalanceContainer>
          <BalanceAfterContainer>
            <Label>{t('transfer.balance-after')}</Label>
            <Address walletAddress={wallet.address} />
          </BalanceAfterContainer>

          <Balance>
            <Numeric
              value={estimatedBalance}
              preset="currency-amount-short"
              currency={currency.ticker}
            />
          </Balance>
          <AmountUsd>
            {estimatedBalanceUsd ? (
              <Numeric
                value={estimatedBalanceUsd}
                preset="currency-amount-short"
                currency="$"
              />
            ) : (
              <Skeleton height={12} width={50} paddingBlock={1} />
            )}
          </AmountUsd>
        </BalanceContainer>
      </Block>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Block = styled.div`
  border-radius: 10px;
  background: var(--backgroundA-3);

  padding-inline: 10px;
  padding-block: 8px;

  display: grid;
  grid-template-columns: 1fr 1fr;
`

const SendContainer = styled.div`
  display: grid;
  border-right: 1px solid var(--background-1);
  align-items: start;
`

const BalanceContainer = styled.div`
  display: grid;
  padding-inline: 14px 0;
  align-items: start;
`

const Label = styled.div`
  color: var(--text-10);
  font-size: 11px;
  font-weight: 500;

  margin-bottom: 2px;
`

const Address = styled(WalletAddressTruncated)`
  color: var(--text-10);
  font-size: 11px;
  font-weight: 500;

  margin-bottom: 2px;
`

const Amount = styled.div`
  color: var(--down-9);
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`
const Balance = styled(Amount)`
  color: var(--text-12);
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`

const AmountUsd = styled.div`
  color: var(--text-9);
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const EstimatedLabel = styled.div`
  font-size: 11px;
  line-height: 16px;
  padding-inline: 6px;
`

const BalanceAfterContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 2px;
`
